import type React from "react"
import { useTranslation } from "react-i18next"
import { Link } from "react-router"

import { Page } from "@/components/page"
import { PageTitle } from "@/components/page-title"
import { useUrl } from "@/utils/url"

import { Box, Link as MuiLink, Stack } from "@mui/material"

import type { ReportCard } from "../../models/report"

interface ReportLayoutProps {
  actions?: React.ReactNode
  children?: React.ReactNode
  details?: React.ReactNode
  filters?: React.ReactNode
  /** Full-height embed mode (Sigma / workbooks). Default: scrolling content. */
  fullHeight?: boolean
  /** PageTitle heading. When omitted, derived from `report.title`. */
  heading?: React.ReactNode
  notifications?: React.ReactNode
  report?: ReportCard
}

const ReportLayout = ({
  actions,
  children,
  details,
  filters,
  fullHeight = false,
  heading,
  notifications,
  report,
}: ReportLayoutProps) => {
  const { t } = useTranslation("reports")
  const { reportingPath } = useUrl()

  const pageHeading = heading ?? (report ? t(report.title) : "")

  return (
    <Page fullHeight={fullHeight}>
      <Stack
        sx={{
          gap: 2,
          ...(fullHeight ? { height: "100%", minHeight: 0 } : {}),
        }}
      >
        <PageTitle
          breadcrumbActions={
            <MuiLink component={Link} to={reportingPath}>
              {t("backToReportsButton")}
            </MuiLink>
          }
          heading={pageHeading}
        />
        {fullHeight ? (
          <Box sx={{ flex: 1, minHeight: 0, position: "relative" }}>
            {children}
          </Box>
        ) : (
          <Box>
            {notifications}
            {filters != null ? (
              <Box
                aria-label="Report filters"
                component="section"
                sx={{ mb: 3 }}
              >
                {filters}
              </Box>
            ) : null}
            {actions != null ? <Box sx={{ mb: 2 }}>{actions}</Box> : null}
            {details}
          </Box>
        )}
      </Stack>
    </Page>
  )
}

export default ReportLayout
